import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {TITLE_SUFFIX} from '../../config/constants';
import {GSSKT_HOST} from '../../config/constants/hosts';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {SponsoredColumnListPage} from '../../features/columns/components/List/SponsoredPage';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../_app';

export type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {},
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    return {
      props: {
        userDataForAnalytics: userDataResponse.ok
          ? userDataResponse.value
          : undefined,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({userDataForAnalytics}: Props) => {
  const router = useRouter();
  const canonicalURL = `${GSSKT_HOST}${decodeURI(router.asPath)}`;

  const title = `企業コラム${TITLE_SUFFIX}`;
  const description =
    'さまざまな企業のコラムを掲載しています。興味がある企業の研究、就活対策に活用しましょう。';
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta key="description" name="description" content={description} />
        <meta
          key="keywords"
          name="keywords"
          content={
            'コラム,就活の情報収集,外資,就活,インターンシップ,新卒採用,会社説明会'
          }
        />
        <link rel="canonical" href={canonicalURL} />
        <meta key="twitter:title" name="twitter:title" content={title} />
        <meta key="og:title" property="og:title" content={title} />
        <meta key="og:url" property="og:url" content={canonicalURL} />
      </Head>
      <SponsoredColumnListPage />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;
Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;
